/**
 * IP 地址校验与提取（纯函数，无任何运行时依赖）。
 *
 * 该模块会被 Edge middleware、RSC、客户端组件同时引用，
 * 禁止引入 next/headers、process 等运行时依赖。
 */

const IPV4_PATTERN = /^((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.){3}(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)$/
const HEX_GROUP_PATTERN = /^[0-9a-fA-F]{1,4}$/

export function isIPv4(address: string): boolean {
  return IPV4_PATTERN.test(address)
}

export function isIPv6(address: string): boolean {
  if (!address.includes(":")) return false

  const parts = address.split("::")
  if (parts.length > 2) return false

  const hasCompression = parts.length === 2
  const head = parts[0]
  const tail = parts[1] ?? ""

  // 除 "::" 压缩符外不允许出现多余的单冒号
  if (head && (head.startsWith(":") || head.endsWith(":"))) return false
  if (tail && (tail.startsWith(":") || tail.endsWith(":"))) return false

  const headGroups = head ? head.split(":") : []
  const tailGroups = tail ? tail.split(":") : []
  const groups = [...headGroups, ...tailGroups]

  // 允许末尾为点分十进制 IPv4（如 64:ff9b::192.0.2.33），其占用两个分组
  let extra = 0
  if (groups.length > 0 && groups[groups.length - 1]?.includes(".")) {
    const dotted = groups.pop() as string
    if (!address.endsWith(dotted) || !isIPv4(dotted)) return false
    extra = 2
  }

  if (!groups.every((group) => HEX_GROUP_PATTERN.test(group))) return false

  const total = groups.length + extra
  return hasCompression ? total <= 7 : total === 8
}

export function isValidIPAddress(address: string): boolean {
  return isIPv4(address) || isIPv6(address)
}

/**
 * 提取并校验 IP 地址，非法输入返回 null。
 *
 * 与 Go net.ParseIP 对齐：trim 后仅接受纯 IPv4/IPv6，不接受端口、方括号或 zone。
 * IPv4 映射地址 ::ffff:192.168.1.1 归一为 192.168.1.1。
 */
export function extractIPAddress(address: string | null | undefined): string | null {
  if (!address) return null
  const candidate = address.trim()
  if (!isValidIPAddress(candidate)) return null

  // IPv6 映射的 IPv4 地址（如 ::ffff:192.168.1.1）
  if (candidate.toLowerCase().startsWith("::ffff:") && candidate.includes(".")) {
    return candidate.slice(7)
  }

  return candidate
}

/**
 * 从入站请求 headers 中提取客户端真实 IP。
 *
 * 优先级：
 * 1. x-forwarded-for：与 HQ 一致，从左向右取第一个合法 IP。
 * 2. x-real-ip：XFF 无合法 IP 时回退，避免优先取到 WAF 节点地址。
 *
 * 依赖 WAF/Nginx 清洗入站转发头；格式校验不验证来源可信性。
 * 无合法 IP 时返回 null，Next headers() 不提供连接地址回退。
 */
export function pickClientIPFromHeaders(headers: Pick<Headers, "get">): string | null {
  const forwardedFor = headers.get("x-forwarded-for")
  if (forwardedFor) {
    const entries = forwardedFor.split(",")
    for (const entry of entries) {
      const ip = extractIPAddress(entry)
      if (ip) return ip
    }
  }

  return extractIPAddress(headers.get("x-real-ip"))
}
